import { FC } from 'react';
import clsx from 'clsx';
import { Button } from '~/shared/components';
import Flex from '~/shared/components/Flex';
import Checkmark from '~/icons/checkmark-alt.svg';
import Minus from '~/icons/minus-alt.svg';
import styles from './RefinementListRow.module.scss';

type RefinementListRowProps = {
    label: string;
    value: string;
    count?: number;
    isRefined: boolean;
    isIndeterminate?: boolean;
    isDisabled?: boolean;
    isSubItem?: boolean;
    /** Smaller row height and checkmark, used by the alphabetic (Brand/Serie/Designer) list. */
    isCompact?: boolean;
    /** Last row of an alphabetic section, for a darker section-break divider. */
    isLastInGroup?: boolean;
    shouldShowCount?: boolean;
    onRefine: (value: string) => void;
};

export const RefinementListRow: FC<RefinementListRowProps> = ({
    label,
    value,
    count,
    isRefined,
    isIndeterminate,
    isDisabled,
    isSubItem,
    isCompact,
    isLastInGroup,
    shouldShowCount,
    onRefine,
}) => (
    <Flex
        grow
        className={clsx(styles.dropdownItemContainer, { [styles.isLastInGroup]: isLastInGroup })}
    >
        <Button
            className={clsx(styles.dropdownItemButton, {
                [styles.isDisabled]: isDisabled,
                [styles.isCompact]: isCompact,
            })}
            fullWidth
            variant="plain"
            disabled={isDisabled}
            onClick={() => onRefine(value)}
            tabIndex={-1}
        >
            <Flex
                grow
                alignItems="center"
                className={clsx(styles.dropdownItemText, {
                    [styles.isCompact]: isCompact,
                    [styles.isRefined]: isRefined,
                })}
            >
                <Flex className={styles.dropdownTextWrapper}>
                    <span
                        className={clsx(styles.checkmarkContainer, {
                            [styles.isRefined]: isRefined,
                            [styles.isIndeterminate]: isIndeterminate,
                            [styles.isSubItem]: isSubItem,
                            [styles.isCompact]: isCompact,
                        })}
                        role="checkbox"
                        aria-label={`${label}, ${count} produkter`}
                        aria-checked={isIndeterminate ? 'mixed' : isRefined}
                        tabIndex={0}
                        onKeyDown={(event) => {
                            if (isDisabled || (event.key !== ' ' && event.key !== 'Enter')) return;
                            event.preventDefault();
                            onRefine(value);
                        }}
                    >
                        {isIndeterminate ? (
                            <Minus
                                className={clsx(styles.checkmark, {
                                    [styles.isCompact]: isCompact,
                                })}
                            />
                        ) : (
                            isRefined && (
                                <Checkmark
                                    className={clsx(styles.checkmark, {
                                        [styles.isCompact]: isCompact,
                                    })}
                                />
                            )
                        )}
                    </span>
                    {label}
                </Flex>
                <span className={styles.dropdownValueWrapper}>
                    {shouldShowCount && count ? count : ''}
                </span>
            </Flex>
        </Button>
    </Flex>
);
